<template>
    <div class="form-group">
        <label class="form-label section-title">{{ $t('messages.brand_safety_segments') }}</label>
        <div class="hint-text small-text">{{ $t('messages.brand_safety_segments_hint') }}</div>
        <div class="segments-container">
            <!-- Левая колонка - доступные сегменты -->
            <div class="available-segments">
                <div class="bordered-container">
                    <!-- Панель управления -->
                    <div class="controls-panel">
                        <div class="search-input">
                            <img src="/images/search.svg" alt="search" class="search-icon" />
                            <input type="text" class="form-input" v-model="searchQuery"
                                :placeholder="$t('messages.search_placeholder')" />
                        </div>
                    </div>

                    <!-- Список сегментов -->
                    <div class="items-list">
                        <div v-for="segment in filteredSegments" :key="segment.id" class="segment-item"
                            v-show="!isSegmentSelected(segment)">
                            <div class="segment-controls">
                                <button class="btn-small" @click="addSegment(segment)">
                                    <img src="/images/circle-check.svg" alt="add" />
                                </button>
                            </div>
                            <div class="segment-content">
                                <div class="segment-title">{{ getSegmentName(segment) }}</div>
                                <div class="segment-description">{{ getSegmentDescription(segment) }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Правая колонка - выбранные сегменты -->
            <div class="selected-segments">
                <div class="bordered-container">
                    <div class="selected-header">
                        <span class="selected-title">{{ $t('messages.selected') }}</span>
                        <button class="btn-delete-all" @click="deleteAllSegments">
                            {{ $t('messages.delete_all') }}
                        </button>
                    </div>

                    <div class="selected-list">
                        <div v-for="segmentId in normalizedSelectedSegments" :key="segmentId" class="segment-item">
                            <div class="segment-controls">
                                <button class="btn-small" @click="removeSegmentById(segmentId)">
                                    <img src="/images/circle-minus.svg" alt="remove" />
                                </button>
                            </div>
                            <div class="segment-content">
                                <div class="segment-title">{{ getSegmentNameById(segmentId) }}</div>
                                <div class="segment-description">{{ getSegmentDescriptionById(segmentId) }}</div>
                            </div>
                        </div>

                        <div v-if="normalizedSelectedSegments.length === 0" class="empty-state">
                            <p>{{ $t('messages.select_segments_from_left') }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "BrandSafetySegments",
    props: {
        segments: {
            type: Array,
            default: () => []
        },
        // selectedSegments may come as an Array of IDs or a semicolon-separated string ("1;2;3")
        selectedSegments: {
            type: [Array, String],
            default: () => []
        }
    },
    data() {
        return {
            searchQuery: ''
        };
    },
    computed: {
        filteredSegments() {
            if (!this.searchQuery) {
                return this.segments;
            }

            const query = this.searchQuery.toLowerCase();
            return this.segments.filter(segment =>
                (segment.segment_en && segment.segment_en.toLowerCase().includes(query)) ||
                (segment.segment_ru && segment.segment_ru.toLowerCase().includes(query)) ||
                (segment.segment_kk && segment.segment_kk.toLowerCase().includes(query)) ||
                (segment.title_en && segment.title_en.toLowerCase().includes(query)) ||
                (segment.title_ru && segment.title_ru.toLowerCase().includes(query)) ||
                (segment.title_kk && segment.title_kk.toLowerCase().includes(query))
            );
        },
        // normalize incoming selectedSegments to an array of numeric IDs
        normalizedSelectedSegments() {
            if (!this.selectedSegments) return [];
            if (Array.isArray(this.selectedSegments)) {
                return this.selectedSegments.map(id => parseInt(id)).filter(id => !isNaN(id));
            }
            if (typeof this.selectedSegments === 'string') {
                return this.selectedSegments
                    .split(';')
                    .map(s => s.trim())
                    .filter(s => s !== '')
                    .map(s => parseInt(s))
                    .filter(id => !isNaN(id));
            }
            return [];
        }
    },
    methods: {
        addSegment(segment) {
            if (!this.isSegmentSelected(segment)) {
                this.$emit('add-segment', segment.id);
            }
        },

        removeSegment(segment) {
            this.$emit('remove-segment', segment.id);
        },

        isSegmentSelected(segment) {
            // Преобразуем ID сегмента в число для сравнения
            const segmentId = typeof segment.id === 'number' ? segment.id : parseInt(segment.id);
            return this.normalizedSelectedSegments.includes(segmentId);
        },

        deleteAllSegments() {
            this.$emit('delete-all-segments');
        },

        removeSegmentById(segmentId) {
            this.$emit('remove-segment', segmentId);
        },

        getSegmentById(segmentId) {
            // Преобразуем segmentId в число для сравнения
            const numericId = parseInt(segmentId);
            return this.segments.find(segment => segment.id === numericId || segment.id === segmentId);
        },

        getSegmentNameById(segmentId) {
            const segment = this.getSegmentById(segmentId);
            return segment ? this.getSegmentName(segment) : 'Unknown';
        },

        getSegmentDescriptionById(segmentId) {
            const segment = this.getSegmentById(segmentId);
            return segment ? this.getSegmentDescription(segment) : '';
        },

        getSegmentName(segment) {
            // Возвращаем название в зависимости от текущего языка платформы
            const currentLocale = this.$i18n.locale || 'ru';

            switch (currentLocale) {
                case 'kk':
                    return segment.segment_kk || segment.segment_ru || segment.segment_en || 'Атауы жоқ';
                case 'en':
                    return segment.segment_en || segment.segment_ru || segment.segment_kk || 'No name';
                case 'ru':
                default:
                    return segment.segment_ru || segment.segment_en || segment.segment_kk || 'Без названия';
            }
        },

        getSegmentDescription(segment) {
            // Возвращаем описание в зависимости от текущего языка платформы
            const currentLocale = this.$i18n.locale || 'ru';

            switch (currentLocale) {
                case 'kk':
                    return segment.title_kk || segment.title_ru || segment.title_en || '';
                case 'en':
                    return segment.title_en || segment.title_ru || segment.title_kk || '';
                case 'ru':
                default:
                    return segment.title_ru || segment.title_en || segment.title_kk || '';
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.segments-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-top: 16px;
}

.available-segments,
.selected-segments {
    .bordered-container {
        border: 1px solid #e9ecef;
        border-radius: 2px;
        background: #fff;
    }
}

.controls-panel {
    display: flex;
    gap: 12px;
    align-items: center;
    background: rgba(245, 245, 245, 1);
    padding: 12px;

    .search-input {
        .form-input {
            width: 100%;
            height: 36px;
            background: white;
        }
    }
}

.selected-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    border-bottom: 1px solid #e9ecef;
    background: rgba(245, 245, 245, 1);

    .selected-title {
        font-weight: 600;
        font-size: 16px;
        color: #212529;
    }
}

.items-list,
.selected-list {
    min-height: 300px;
    max-height: 400px;
    overflow-y: auto;
    padding: 16px;
}

.segment-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    margin-bottom: 8px;
    background: #f8f9fa;
    transition: all 0.2s;

    &:hover {
        background: #e9ecef;
        border-color: #dee2e6;
    }
}

.segment-controls {
    display: flex;
    flex-direction: row;
    gap: 8px;

    // Стили кнопок теперь импортируются из _buttons.scss

    .btn-remove-selected {
        margin-left: auto;
    }
}

.segment-content {
    flex: 1;

    .segment-title {
        font-size: 14px;
        font-weight: 500;
        color: #212529;
        margin-bottom: 4px;
    }

    .segment-description {
        font-size: 12px;
        color: #6c757d;
        line-height: 1.4;
    }
}

.hint-text {
    margin-top: 6px;
    color: #6c757d;
    font-size: 12px;
    line-height: 1.3;
}

.empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: #6c757d;
    font-style: italic;
}
</style>

